/**
 * The Visit page — `docs/visit-page.md` §5.1, §7 – §19 (Boards 1, 13 – 16,
 * 17 H): one visit (a `work_items` row) and every day of it, on the record
 * shell (`RecordPage`) with the additive `lead` tabs and the opt-in stacked
 * header (lead decision 13).
 *
 * `/visits/:id` takes a work item's id or reference (*V-1187*); a day's id
 * lands on its visit with `?day=<n>` (§5.1). `?tab=` opens a tab, `?day=n` a
 * day's sheet. Everything drawn is the server's `VisitRead`; the page never
 * computes a number (§2.1).
 *
 * The tabs are the other slices' components, mounted by name; the sheets the
 * header, the Overview and the rail open are theirs too (the exports registry,
 * lead round 3).
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  firstNameOf, headerButtons, MARK_COLOUR_NAMES, MARK_PALETTE, type MarkColourName, type OrderAnswer, type RecordType, type VisitRead, type VisitTabKey,
} from '@rowted/shared'
import { request } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import { EM_DASH } from '../lib/format.js'
import { mapsLink } from '../lib/maps.js'
import { usePhone } from '../lib/use-narrow.js'
import { completeActivity, type RecordSummary } from '../lib/use-record.js'
import { EmptyState } from '../components/primitives.js'
import type { MenuItem } from '../components/context-menu.js'
import { Glyph } from '../frame/mark-glyphs.js'
import { RecordPage, type RecordConfig } from '../record/record-page.js'
import type { RecordAction, RecordMeta, RecordTag } from '../record/record-header.js'
import type { RecordTab } from '../record/record-tabs.js'
import { renderStandardTab, standardTabs } from '../record/record-standard.js'
import { TextSheet } from '../activity/new/text-sheet.js'
import { applyAnswer, useVisitRead, useVisitWrite, VisitPageProvider, type VisitWriteResult } from './api.js'
import { COMMON, HEADER, PHONE, SERIES, STATES, TABS } from './copy.js'
import { dayRange, dayText, hours1, hoursBare, money, percent, weekdayName } from './format.js'
import { VisitGlyph, type VisitGlyphName } from './glyphs.js'
import { VisitMark } from './visit-mark.js'
import { VisitRailCards } from './rail.js'
import { VisitOverview, type OverviewHandlers } from './overview.js'
import { PhoneOverview, PhoneSectionLabel } from './phone.js'
import { VISIT_LAYER, VisitButton, VisitPopup } from './sheet.js'
import { MaterialsTab } from './materials-tab.js'
import { EquipmentTab } from './equipment-tab.js'
import { TimeTab } from './time-tab.js'
import { MoneyTab } from './money-tab.js'
import { ForCrewTab } from './for-crew-tab.js'
import { FromCrewTab } from './from-crew-tab.js'
import { DaySheet } from './day-sheet.js'
import { AddDayPopup } from './add-day.js'
import { ReviewCloseSheet } from './review-close.js'
import { AdjustPopup } from './adjust.js'
import { CloseOutForCrewSheet } from './close-out-for-crew.js'
import { OrderSheet, OrderToast } from './order-sheet.js'
import { RentSheet } from './rent-sheet.js'
import { CrewNoteSheet } from './crew-note-sheet.js'
import { CrewPreviewSheet } from './crew-preview-sheet.js'
import { PhotoLightbox } from './photo-lightbox.js'
import { useVisitUpload } from './visit-upload.js'
import { CancelVisit } from './cancel-visit.js'
import { EditVisitSheet } from './edit-visit.js'

/**
 * The record kind the shell's Activity, Files and History use for a visit — a
 * work item (lead decision 17). `visit` keeps meaning a day.
 */
const RECORD_OBJECT = 'work_item' as RecordType

/** The six tabs that lead (§7.1, do-not-substitute 3) — their icons and their words. */
const LEAD_TABS: { key: VisitTabKey; icon: RecordTab['icon'] }[] = [
  { key: 'materials', icon: 'material' },
  { key: 'equipment', icon: 'loader' },
  { key: 'time', icon: 'hourglass' },
  { key: 'money', icon: 'dollar' },
  { key: 'for-crew', icon: 'clipboard' },
  { key: 'from-crew', icon: 'camera' },
]

/** What a sheet opened from the page is. */
type Open =
  | { kind: 'addDay' } | { kind: 'review' } | { kind: 'adjust' } | { kind: 'closeOut'; day: number }
  | { kind: 'order'; lineId: string } | { kind: 'book'; rentalId: string } | { kind: 'note' } | { kind: 'preview' }
  | { kind: 'photo'; id: string } | { kind: 'cancel' } | { kind: 'hold' } | { kind: 'text'; contactId: string }
  | { kind: 'edit' }
  | null

/** Below the desktop's 1280: the iPad (Board 15). */
function useMedia(query: string): boolean {
  const [on, setOn] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches)
  useEffect(() => {
    const list = window.matchMedia(query)
    const update = () => setOn(list.matches)
    update()
    list.addEventListener('change', update)
    return () => list.removeEventListener('change', update)
  }, [query])
  return on
}

export function VisitPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { data, error, refresh } = useVisitRead(id)

  /* A day's id lands on its visit, with that day's sheet open (§5.1). */
  useEffect(() => {
    if (!data || data.id === id || data.reference === id) return
    const day = data.overview.days.days.find((tile) => tile.day.visitId === id)
    navigate(`/visits/${data.id}${day ? `?day=${day.day.index}` : ''}`, { replace: true })
  }, [data, id, navigate])

  if (error) {
    return (
      <EmptyState
        title={HEADER.breadcrumb}
        body={error}
        action={{ label: HEADER.breadcrumb, onClick: () => navigate('/visits') }}
      />
    )
  }
  if (!data) return <div className="flex-1 bg-bg" data-visit-loading="" />
  return <LoadedVisit initial={data} onRefresh={refresh} />
}

function LoadedVisit({ initial, onRefresh }: { initial: VisitRead; onRefresh: (next?: VisitRead) => void }) {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [read, setReadState] = useState<VisitRead>(initial)
  useEffect(() => { setReadState(initial) }, [initial])
  const phone = usePhone()
  const tablet = useMedia('(max-width: 1279px)') && !phone
  const portrait = useMedia('(max-width: 1023px)') && !phone
  const [open, setOpen] = useState<Open>(null)
  const [ordered, setOrdered] = useState<OrderAnswer['ordered'] | null>(null)
  const [people, setPeople] = useState<{ id: string; name: string }[]>([])
  const [busyActivity, setBusyActivity] = useState<string | null>(null)
  const write = useVisitWrite(read.id)

  const setRead = useCallback((next: VisitRead) => { setReadState(next); onRefresh(next) }, [onRefresh])
  const reload = useCallback(() => {
    void request<VisitRead>(`/visits/${read.id}`).then(setRead).catch(() => undefined)
  }, [read.id, setRead])
  const apply = useCallback((answer: Parameters<typeof applyAnswer>[1]) => {
    setReadState((was) => applyAnswer(was, answer))
    /* A tab's write answers its own tab; the Overview and the rail it moved are read again behind it (§5.2). */
    if (answer.tab.key !== 'overview') reload()
  }, [reload])
  const onWritten = useCallback((result?: VisitWriteResult) => {
    if (result?.answer?.tab.key === 'overview') setRead(result.answer.tab.read)
    else reload()
  }, [reload, setRead])

  useEffect(() => {
    void request<{ items: { id: string; name: string }[] }>('/people?limit=200')
      .then((body) => setPeople(body.items))
      .catch(() => setPeople([]))
  }, [])

  const upload = useVisitUpload(read.id, read.orgUnitId, onWritten, 'overview')

  const day = Number(params.get('day') ?? '') || null
  const closeDay = () => {
    const next = new URLSearchParams(params)
    next.delete('day')
    setParams(next, { replace: true })
  }
  const openDay = (n: number) => {
    const next = new URLSearchParams(params)
    next.set('day', String(n))
    setParams(next, { replace: true })
  }
  const openHref = useCallback((href: string) => navigate(href), [navigate])
  /* `?edit=1` opens the visit's edit sheet (§7.3) — About *Edit*, *Edit plan*, the phone's pencil. */
  const editing = params.get('edit') === '1'
  const closeEdit = () => {
    setOpen(null)
    if (!editing) return
    const next = new URLSearchParams(params)
    next.delete('edit')
    setParams(next, { replace: true })
  }

  const handlers: OverviewHandlers = {
    onOpen: openHref,
    onOpenDay: openDay,
    onAddDay: read.can.edit ? () => setOpen({ kind: 'addDay' }) : undefined,
    onOrder: read.can.edit ? (lineId) => setOpen({ kind: 'order', lineId }) : undefined,
    onBook: read.can.edit ? (rentalId) => setOpen({ kind: 'book', rentalId }) : undefined,
    onPreview: () => setOpen({ kind: 'preview' }),
    onAddNote: read.can.edit ? () => setOpen({ kind: 'note' }) : undefined,
    onEditSeriesNote: read.can.edit
      ? (noteId) => openHref(`/visits/${read.id}?tab=for-crew${noteId ? `&edit=${encodeURIComponent(noteId)}` : ''}`)
      : undefined,
    onPhoto: (photoId) => setOpen({ kind: 'photo', id: photoId }),
    onAdjust: read.can.close && read.status === 'closed' ? () => setOpen({ kind: 'adjust' }) : undefined,
  }

  const data: PageData = {
    read, phone, tablet, portrait,
    open: setOpen,
    navigate: openHref,
    upload: () => upload.openPicker({ photo: true }),
    release: async () => { const result = await write('POST', '/release', { reason: '' }, { tab: 'overview' }); onWritten(result) },
    textContact: (contactId) => setOpen({ kind: 'text', contactId }),
  }
  const config = useMemo(() => visitConfig(), [])
  const summary: RecordSummary = useMemo(() => summaryOf(read, tablet), [read, tablet])

  return (
    <VisitPageProvider value={{ id: read.id, read, setRead, apply }}>
      {/* The boards' page type: line-height normal, tabular figures (every vp-*.html body). */}
      <div className="flex min-h-0 flex-1 flex-col leading-[normal] tabular-nums [&_button]:tabular-nums" data-as-drawn={phone ? 'vp-16 page' : undefined} data-visit-page={read.reference}>
        <RecordPage config={config} record={data} summary={summary}>
          {(tab) => {
            const shown = tabsOf(read).find((each) => each.key === tab)
            const body = tab === 'overview'
              ? (phone ? <PhoneOverview read={read} handlers={handlers} /> : <VisitOverview read={read} handlers={handlers} tablet={tablet} portrait={portrait} />)
              : leadTab(tab, read, phone) ?? renderStandardTab(tab, {
                object: RECORD_OBJECT,
                id: read.id,
                href: `/visits/${read.id}`,
                summary,
                people,
                orgUnitId: read.orgUnitId,
                history: {
                  table: 'work_items',
                  related: { tables: ['visits'], recordIds: read.overview.days.days.map((tile) => tile.day.visitId) },
                },
                busyId: busyActivity,
                canEdit: read.can.edit,
                onComplete: (activityId) => {
                  setBusyActivity(activityId)
                  void completeActivity(activityId).finally(() => { setBusyActivity(null); reload() })
                },
                onChanged: reload,
                onUploadReady: () => undefined,
                onOpen: openHref,
              })
            return phone
              /* The label sits 8 above its first card (Board 16), not a main-column gap away. */
              ? <div className="flex min-w-0 flex-col"><PhoneSectionLabel icon={shown?.icon} label={shown?.label ?? ''} />{body}</div>
              : body
          }}
        </RecordPage>
      </div>

      {upload.input}

      {day !== null && <DaySheet visitId={read.id} read={read} day={day} onClose={closeDay} />}
      {open?.kind === 'addDay' && <AddDayPopup visitId={read.id} read={read} onClose={() => setOpen(null)} onAdded={() => setOpen(null)} />}
      {open?.kind === 'review' && <ReviewCloseSheet visitId={read.id} read={read} onClose={() => setOpen(null)} onClosed={() => setOpen(null)} />}
      {open?.kind === 'adjust' && <AdjustPopup visitId={read.id} read={read} onClose={() => setOpen(null)} onAdjusted={(result) => { setOpen(null); onWritten(result) }} />}
      {open?.kind === 'closeOut' && (
        <CloseOutForCrewSheet visitId={read.id} read={read} day={open.day} onClose={() => setOpen(null)} onClosedOut={() => setOpen(null)} />
      )}
      {open?.kind === 'order' && (
        <OrderSheet read={read} lineId={open.lineId} phone={phone} tab="overview" onClose={() => setOpen(null)}
          onDone={(result, next) => { setOpen(null); onWritten(result); setOrdered(next) }} />
      )}
      {ordered && (
        <OrderToast read={read} ordered={ordered} tab="overview" phone={phone}
          onUndone={(result) => { setOrdered(null); onWritten(result) }} onClose={() => setOrdered(null)} />
      )}
      {open?.kind === 'book' && (
        <RentSheet visitId={read.id} read={read} rentalId={open.rentalId} tab="overview" phone={phone}
          onClose={() => setOpen(null)} onWritten={(result) => { setOpen(null); onWritten(result) }} />
      )}
      {open?.kind === 'note' && <CrewNoteSheet visitId={read.id} read={read} onClose={() => setOpen(null)} onWritten={(result) => { setOpen(null); onWritten(result) }} />}
      {open?.kind === 'preview' && (
        <CrewPreviewSheet visitId={read.id} read={read} onClose={() => setOpen(null)}
          onOpenTab={(key) => { setOpen(null); openHref(`/visits/${read.id}?tab=${key}`) }} />
      )}
      {open?.kind === 'photo' && (
        <PhotoLightbox visitId={read.id} read={read} photos={read.overview.fromCrew.photos} openId={open.id}
          customerFirstName={read.header.meta.contact ? firstNameOf(read.header.meta.contact.name) : null}
          tab="overview" onClose={() => setOpen(null)} onWritten={onWritten} />
      )}
      {open?.kind === 'cancel' && <CancelVisit visit={read.id} onClose={() => setOpen(null)} onCancelled={() => { setOpen(null); reload() }} />}
      {open?.kind === 'hold' && <HoldPopup read={read} onClose={() => setOpen(null)} onHeld={(result) => { setOpen(null); onWritten(result) }} />}
      {open?.kind === 'text' && (
        <TextSheet prefill={{ contactId: open.contactId, links: [{ type: 'job', id: read.header.meta.job.id, label: read.header.meta.job.reference }] }}
          phone={phone} onClose={() => setOpen(null)} onSwitch={() => setOpen(null)} onMade={() => { setOpen(null); reload() }} />
      )}
      {(open?.kind === 'edit' || editing) && read.rail.about.canEdit && (
        <EditVisitSheet read={read} onClose={closeEdit} onWritten={(result) => { closeEdit(); onWritten(result) }} />
      )}
    </VisitPageProvider>
  )
}

// ---------------------------------------------------------------------------
// The configuration
// ---------------------------------------------------------------------------

interface PageData {
  read: VisitRead
  phone: boolean
  tablet: boolean
  portrait: boolean
  open: (next: Open) => void
  navigate: (href: string) => void
  upload: () => void
  release: () => Promise<void>
  textContact: (contactId: string) => void
}

const glyph = (name: VisitGlyphName, size = 14) => <VisitGlyph name={name} size={size} stroke={2} />

/** The tabs (Board 1): Overview, the six that lead, then the shell's Activity · Files · History. */
function tabsOf(read: VisitRead): RecordTab[] {
  const counts: Record<string, number> = {}
  for (const tab of read.tabs) counts[tab.key] = tab.count
  const present = new Set(read.tabs.map((tab) => tab.key))
  return standardTabs({
    counts,
    lead: LEAD_TABS.filter((tab) => present.has(tab.key)).map((tab) => ({
      key: tab.key,
      label: TABS[tab.key as keyof typeof TABS],
      icon: tab.icon,
      count: counts[tab.key] || undefined,
      /* No tab's board draws the rail (Boards 2, 5, 7, 8, 9, 10). */
      wide: true,
    })),
  })
}

function leadTab(tab: string, read: VisitRead, phone: boolean): ReactNode | null {
  const props = { visitId: read.id, read, phone }
  switch (tab) {
    case 'materials': return <MaterialsTab {...props} />
    case 'equipment': return <EquipmentTab {...props} />
    case 'time': return <TimeTab {...props} />
    case 'money': return <MoneyTab {...props} />
    case 'for-crew': return <ForCrewTab {...props} />
    case 'from-crew': return <FromCrewTab {...props} />
    default: return null
  }
}

/** The pills and counts the shell draws, from the read model (§5.2) — never a second request. */
function summaryOf(read: VisitRead, tablet: boolean): RecordSummary {
  const { pills } = read.header
  const out: RecordSummary['pills'] = [{
    value: COMMON.h(hours1(pills.hours.hours)),
    label: tablet || pills.hours.kind === 'actual'
      ? HEADER.pills.hoursOf(hoursBare(pills.hours.planned))
      : HEADER.pills.hoursSoFar(hoursBare(pills.hours.planned)),
  }]
  if (pills.internal) {
    const { money: amount, margin } = pills.internal
    /* *—* until something on the visit costs anything — never *$0* or *100.0%* (walkthrough Oct 30, §9). */
    out.push({
      value: amount.amount === null ? EM_DASH : money(amount.amount),
      label: tablet && amount.kind === 'projected_cost' ? COMMON.projected : HEADER.pills.money[amount.kind],
      ...(amount.locked ? { internal: true } : {}),
    })
    out.push({
      value: margin.percent === null ? EM_DASH : percent(margin.percent),
      label: tablet ? COMMON.margin : HEADER.pills.margin[margin.kind],
      internal: true,
    })
  }
  const counts: Record<string, number> = {}
  for (const tab of read.tabs) counts[tab.key] = tab.count
  return { pills: out, counts, nextUp: [], peeks: { jobs: [], visits: [], notes: [] } }
}

/**
 * A division's tag in the palette pair the boards draw (Hardscape teal,
 * Maintenance green …), by the division's palette name — `divisions.colour`
 * is one of the ten `MARK_PALETTE` colours (migration 0103), named here so no
 * hex sits in the page.
 */
const DIVISION_TONES: Record<MarkColourName, { fg: string; bg: string }> = {
  green: { fg: 'rgb(var(--color-accent-dark))', bg: 'rgb(var(--color-accent-soft))' },
  teal: { fg: 'rgb(var(--color-teal-band-text))', bg: 'rgb(var(--color-teal-band-bg))' },
  amber: { fg: 'rgb(var(--color-amber-band-text))', bg: 'rgb(var(--color-amber-band-bg))' },
  blue: { fg: 'rgb(var(--color-info))', bg: 'rgb(var(--color-info-bg))' },
  purple: { fg: 'rgb(var(--status-kind-fee-fg))', bg: 'rgb(var(--status-kind-fee-bg))' },
  indigo: { fg: 'rgb(var(--status-job-reviewed-fg))', bg: 'rgb(var(--status-job-reviewed-bg))' },
  red: { fg: 'rgb(var(--color-danger))', bg: 'rgb(var(--color-danger-bg))' },
  cyan: { fg: 'rgb(var(--status-job-in_progress-fg))', bg: 'rgb(var(--status-job-in_progress-bg))' },
  orange: { fg: 'rgb(var(--color-warn))', bg: 'rgb(var(--color-warn-bg))' },
  slate: { fg: 'rgb(var(--color-slate-band-text))', bg: 'rgb(var(--color-slate-band-bg))' },
}

function divisionTone(colour: string | null): { fg: string; bg: string } | undefined {
  const value = (colour ?? '').toLowerCase()
  const name = MARK_COLOUR_NAMES.find((each) => each === value || MARK_PALETTE[each] === value)
  return name ? DIVISION_TONES[name] : undefined
}

function tagsOf(read: VisitRead, portrait: boolean): RecordTag[] {
  const { tags } = read.header
  const status = tags.status
  const out: RecordTag[] = [{
    label: HEADER.status[status as keyof typeof HEADER.status] ?? HEADER.status.in_progress,
    tone: { fg: `rgb(var(--status-visit-${status}-fg))`, bg: `rgb(var(--status-visit-${status}-bg))` },
  }]
  if (tags.division) {
    const tone = divisionTone(tags.division.colour)
    out.push({
      label: tags.division.name,
      icon: <Glyph set="division" slug={tags.division.glyph} size={11} strokeWidth={2.2} />,
      ...(tone ? { tone } : {}),
    })
  }
  if (!portrait) out.push({ label: HEADER.billing[tags.billing] })
  return out
}

/** The calendar glyph's words (§7.2) — *Wed Oct 21 – Fri Oct 23 · Day 2 of 3*. */
function datesWords(read: VisitRead, tablet: boolean): string | null {
  const { dates } = read.header.meta
  if (read.series && read.series.count !== null && read.series.weekday !== null && read.series.last) {
    return HEADER.meta.series(weekdayName(read.series.weekday), dayRange(read.series.first, read.series.last, 'monthDay'),
      String(read.series.index), String(read.series.count))
  }
  if (tablet) return dates.todayIndex !== null ? COMMON.dayOf(String(dates.todayIndex), String(dates.dayCount)) : null
  if (!dates.first) {
    if (dates.was) return STATES.was(dayText(dates.was, 'date'))
    return dates.from ? COMMON.from(dayText(dates.from, 'monthDay')) : null
  }
  if (dates.dayCount <= 1) return HEADER.meta.oneDay(dayText(dates.first, 'date'))
  const range = dayRange(dates.first, dates.last ?? dates.first, 'long')
  return dates.todayIndex !== null
    ? HEADER.meta.daysToday(range, String(dates.todayIndex), String(dates.dayCount))
    : HEADER.meta.days(range, String(dates.dayCount))
}

function metaOf(data: PageData): RecordMeta[] {
  const { read, tablet, navigate } = data
  const { meta } = read.header
  const out: RecordMeta[] = [{
    icon: glyph('job'),
    text: tablet ? meta.job.reference : COMMON.dot(meta.job.reference, meta.job.name),
    onClick: () => navigate(`/jobs/${meta.job.id}`),
  }]
  if (meta.property) {
    out.push({
      icon: glyph('pin'),
      text: tablet ? meta.property.name : COMMON.dot(meta.property.name, `${meta.property.line1}, ${meta.property.city}`),
      onClick: () => navigate(`/properties/${meta.property!.id}`),
    })
  }
  if (meta.contact && !tablet) {
    out.push({ icon: glyph('person'), text: meta.contact.title ? HEADER.meta.contact(meta.contact.name, meta.contact.title) : meta.contact.name })
  }
  const dates = datesWords(read, tablet)
  if (dates) out.push({ icon: glyph('calendar'), text: dates })
  if (meta.crew) {
    out.push({
      icon: glyph('people'),
      text: tablet || !meta.crew.lead ? meta.crew.crew.name : HEADER.meta.crew(meta.crew.crew.name, meta.crew.lead.name),
    })
  }
  const maps = meta.property ? mapsLink([meta.property.line1, meta.property.city]) : null
  const live = read.status !== 'closed' && read.status !== 'cancelled' && read.series === null
  if (maps && live && !tablet) out.push({ text: HEADER.meta.directions, href: maps.href })
  return out
}

/** §17.3's buttons, as the shell's actions, primary and ⋯ menu. */
function buttonsOf(data: PageData): { actions: RecordAction[]; primary: RecordAction | null; menu: MenuItem[] } {
  const { read, portrait, open, navigate, tablet } = data
  /* The buttons' glyphs — 14.5 on a desktop (Board 1), 15 on an iPad (Board 15). */
  const size = tablet ? 15 : 14.5
  const buttons = headerButtons({ status: read.status, series: read.series !== null, can: read.can })
  const jobHref = `/jobs/${read.header.meta.job.id}`
  const secondary: RecordAction | null = !buttons.secondary ? null : {
    label: HEADER.secondary[buttons.secondary],
    icon: buttons.secondary === 'message_crew' || buttons.secondary === 'message' ? glyph('bubble', size)
      : buttons.secondary === 'open_job' ? glyph('job', size) : undefined,
    onClick: () => {
      if (buttons.secondary === 'message_crew' || buttons.secondary === 'message') open({ kind: 'note' })
      else if (buttons.secondary === 'open_job') navigate(jobHref)
      else if (buttons.secondary === 'open_series') navigate(`${jobHref}?tab=visits`)
      else open({ kind: 'edit' })
    },
  }
  const primary: RecordAction | null = !buttons.primary ? null : {
    label: HEADER.primary[buttons.primary],
    icon: glyph(buttons.primary === 'review_close' ? 'check' : buttons.primary === 'take_off_hold' ? 'play' : 'calendar', size),
    onClick: () => {
      if (buttons.primary === 'review_close') open({ kind: 'review' })
      else if (buttons.primary === 'take_off_hold') void data.release()
      else navigate(`/schedule?visit=${encodeURIComponent(read.id)}`)
    },
  }
  const menu: MenuItem[] = []
  /* iPad portrait (§19.1): *Message crew* moves into ⋯. */
  if (portrait && secondary && buttons.secondary === 'message_crew') {
    menu.push({ key: 'message', label: secondary.label, onSelect: secondary.onClick })
  }
  for (const item of buttons.menu) {
    menu.push({
      key: item,
      label: HEADER.menu[item],
      ...(item === 'cancel_visit' ? { danger: true } : {}),
      onSelect: () => {
        if (item === 'add_day') open({ kind: 'addDay' })
        else if (item === 'put_on_hold') open({ kind: 'hold' })
        else if (item === 'cancel_visit') open({ kind: 'cancel' })
        else if (item === 'print_crew_sheet') {
          const today = read.header.meta.dates.todayIndex
          window.open(`/visits/${read.id}/crew-sheet${today ? `?day=${today}` : ''}`, '_blank', 'noopener')
        } else if (item === 'copy_link') {
          void navigator.clipboard?.writeText(`${window.location.origin}/visits/${read.reference}`)
        }
      },
    } as MenuItem)
  }
  return {
    actions: secondary && !(portrait && buttons.secondary === 'message_crew') ? [secondary] : [],
    primary,
    menu,
  }
}

function visitConfig(): RecordConfig<PageData> {
  return {
    object: 'visit',
    headerLayout: 'stacked',
    tabletGutters: 'wide',
    breadcrumb: ({ read }) => [{ label: HEADER.breadcrumb, href: '/visits' }, { label: read.reference }],
    mark: ({ read }) => <VisitMark mark={read.header.mark} size={56} />,
    /* A series' name with its place — *Weekly mowing · 26 of 30* (§3.2); the read sends the name bare. */
    title: ({ read }) => (read.series && read.series.count !== null
      ? SERIES.name(read.header.name, String(read.series.index), String(read.series.count))
      : read.header.name),
    tags: (data) => tagsOf(data.read, data.portrait),
    meta: metaOf,
    actions: (data) => buttonsOf(data).actions,
    primary: (data) => buttonsOf(data).primary,
    menu: (data) => buttonsOf(data).menu,
    belowHeader: ({ read, phone }) => (phone ? null : <UnderHeader read={read} />),
    tabs: ({ read }) => tabsOf(read),
    rail: (data) => (data.phone ? null : (
      <VisitRailCards
        read={data.read}
        handlers={{
          onOpen: data.navigate,
          ...(data.read.rail.about.canEdit ? { onEdit: () => data.open({ kind: 'edit' }) } : {}),
          onTextContact: data.textContact,
          onCloseOut: (day) => data.open({ kind: 'closeOut', day }),
        }}
      />
    )),
    phone: {
      quickActions: ({ read, upload }) => {
        const lead = read.rail.people.people.find((row) => row.role === 'crew_lead')
        const maps = read.header.meta.property ? mapsLink([read.header.meta.property.line1, read.header.meta.property.city]) : null
        return [
          ...(lead ? [{
            label: PHONE.call(lead.person.firstName),
            icon: <VisitGlyph name="phone" size={20} stroke={2} />,
            ...(lead.phone ? { href: `tel:${lead.phone.replace(/[^\d+]/g, '')}` } : {}),
          }] : []),
          ...(maps ? [{ label: PHONE.navigate, icon: <VisitGlyph name="navigate" size={20} stroke={2} />, href: maps.href }] : []),
          { label: PHONE.photo, icon: <VisitGlyph name="camera" size={20} stroke={2} />, onClick: upload },
        ]
      },
      tabBar: ['overview', 'materials', 'time', 'money'],
      primaryByTab: (tab, data) => {
        const { read, open, navigate, upload } = data
        const go = (query: string) => () => navigate(`/visits/${read.id}?tab=${query}`)
        const icon = (name: VisitGlyphName) => <VisitGlyph name={name} size={20} stroke={2} />
        switch (tab) {
          case 'overview': return read.can.close && read.status !== 'closed' ? { label: PHONE.green.overview, icon: icon('check'), onClick: () => open({ kind: 'review' }) } : null
          case 'materials': return read.quick.materialsToOrder > 0
            ? { label: PHONE.green.materials(String(read.quick.materialsToOrder)), icon: icon('cart'), onClick: go('materials&order=next') }
            : { label: PHONE.green.materialsNone, icon: icon('plus'), onClick: go('materials&add=1') }
          case 'equipment': return { label: PHONE.green.equipment, icon: icon('truck'), onClick: go('equipment&rent=1') }
          case 'time': return { label: PHONE.green.time, icon: icon('clock'), onClick: go('time&add=1') }
          case 'money': return { label: PHONE.green.money, icon: icon('receipt'), onClick: go('money&add=1') }
          case 'for-crew': return { label: PHONE.green.forCrew, icon: icon('plus'), onClick: go('for-crew&note=1') }
          case 'from-crew':
          case 'files': return { label: PHONE.green.upload, icon: icon('upload'), onClick: upload }
          case 'activity':
          case 'history': return { label: PHONE.green.log, icon: icon('bubble'), onClick: go('activity') }
          default: return null
        }
      },
      subline: ({ read }) => {
        const { meta } = read.header
        const n = meta.dates.todayIndex
        return n !== null && meta.property
          ? PHONE.sub(meta.job.reference, meta.property.name, String(n), String(meta.dates.dayCount))
          : COMMON.dot(meta.job.reference, meta.property?.name ?? meta.job.name)
      },
      onEdit: ({ read, open }) => (read.rail.about.canEdit ? () => open({ kind: 'edit' }) : null),
    },
  }
}

// ---------------------------------------------------------------------------
// The line under the header — Board 13 B, §17.3
// ---------------------------------------------------------------------------

function UnderHeader({ read }: { read: VisitRead }) {
  const note = read.header.note
  if (!note || note.kind === 'closed') return null
  const tz = read.timeZone
  const shortDay = (iso: string) => new Intl.DateTimeFormat('en-US', { timeZone: tz, month: 'short', day: 'numeric' }).format(new Date(iso))
  let body: ReactNode
  if (note.kind === 'unscheduled') body = <p className="m-0">{STATES.unscheduled}</p>
  else if (note.kind === 'prep') {
    body = (
      <>
        <div className="flex items-center gap-2.5 self-start rounded-[10px] border border-warn-line bg-amber-row px-2.5 py-2 text-[12.5px] text-text">
          <span className="inline-flex h-[22px] shrink-0 items-center gap-[5px] rounded-full bg-warn-bg px-2 text-[11.5px] font-semibold text-warn">
            <i className="inline-block h-1.5 w-1.5 rounded-full bg-warn" />
            {COMMON.prep(String(note.ready), String(note.total))}
          </span>
          {STATES.prepLine(note.toOrder, note.toLoad)}
        </div>
        <p className="m-0">{STATES.prep(dayText(note.nextDay, 'weekday'))}</p>
      </>
    )
  } else if (note.kind === 'skipped') body = <p className="m-0">{STATES.skipped(note.by.name, shortDay(note.at), note.reason)}</p>
  else if (note.kind === 'on_hold') body = <p className="m-0">{STATES.onHold(note.by.name, shortDay(note.at), note.reason)}</p>
  else body = <p className="m-0">{STATES.cancelled(note.by.name)}</p>
  return (
    <div className={cn('-mt-1.5 flex flex-col gap-2.5 pb-3.5 pl-[98px] pr-6 text-[12px] leading-[1.45] text-text-body')} data-header-note={note.kind}>
      {body}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Put on hold — §17, *asks why*
// ---------------------------------------------------------------------------

function HoldPopup({ read, onClose, onHeld }: { read: VisitRead; onClose: () => void; onHeld: (result: VisitWriteResult) => void }) {
  const write = useVisitWrite(read.id)
  const [why, setWhy] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const phone = usePhone()
  const area = useRef<HTMLTextAreaElement>(null)
  useEffect(() => { area.current?.focus() }, [])
  const save = async () => {
    setBusy(true)
    try {
      onHeld(await write('POST', '/hold', { reason: why.trim() }, { tab: 'overview' }))
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : String(cause))
      setBusy(false)
    }
  }
  return (
    <VisitPopup
      width={470}
      layer={VISIT_LAYER}
      title={HEADER.menu.put_on_hold}
      onClose={onClose}
      footer={(
        <>
          <VisitButton size={phone ? 'phone' : 'popup'} onClick={onClose}>{COMMON.cancel}</VisitButton>
          <VisitButton size={phone ? 'phone' : 'popup'} tone="primary" disabled={busy || why.trim() === ''} onClick={() => void save()}>
            {HEADER.menu.put_on_hold}
          </VisitButton>
        </>
      )}
    >
      <label className="flex flex-col gap-1.5">
        <span className="text-fieldlabel text-text-muted">{COMMON.why}</span>
        <textarea ref={area} value={why} onChange={(event) => setWhy(event.target.value)} rows={3}
          className="w-full resize-none rounded-control border border-line bg-surface px-3 py-2 text-[13px] outline-none focus:border-accent-line focus:shadow-[0_0_0_2px_rgb(var(--color-accent-ring))]" />
      </label>
      {problem && <p className="m-0 mt-2 text-[12px] text-danger">{problem}</p>}
    </VisitPopup>
  )
}
